package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.charts.svg.BarBucket
import com.arenaproplay.admin.ui.components.charts.svg.BarColumns
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.ui.components.forms.AddressAutocompleteField
import com.arenaproplay.admin.ui.components.forms.CoordinatesHint
import com.arenaproplay.admin.ui.components.forms.FormButton
import com.arenaproplay.admin.ui.components.forms.OTHER_SPORT
import com.arenaproplay.admin.ui.components.forms.SportTypeSelect
import com.arenaproplay.admin.ui.components.forms.TextArea
import com.arenaproplay.admin.ui.components.forms.TextInput
import com.arenaproplay.admin.ui.components.notifications.LocalToastState
import com.arenaproplay.admin.ui.components.overlay.ConfirmActionOverlay
import com.arenaproplay.admin.ui.components.overlay.Modal
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.localization.LocalAppStrings
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.numSmall
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.periodFromUrl
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*

// ── Avatar gradient — deterministic per organizer name ────────────────────────

private fun organizerAvatarGradient(name: String): String {
    val idx = kotlin.math.abs(name.hashCode()) % AppColors.AvatarPalette.size
    return AppColors.AvatarPalette[idx]
}

private fun organizerInitials(name: String): String {
    val words = name.trim().split(" ").filter { it.isNotEmpty() }
    return when {
        words.size >= 2 -> "${words[0].first().uppercaseChar()}${words[1].first().uppercaseChar()}"
        words.size == 1 && words[0].length >= 2 ->
            "${words[0][0].uppercaseChar()}${words[0][1].uppercaseChar()}"
        words.size == 1 -> words[0].first().uppercaseChar().toString()
        else -> "?"
    }
}

// ── Event status → Pill tone ──────────────────────────────────────────────────
// upcoming / scheduled → Accent (blue)
// active / live / ongoing → Success (green)
// finished / completed → Neutral (grey)
// cancelled → Danger (red)
// eventStatusPillTone() is defined in Pill.kt as a top-level shared function.

// Backend enum: com.proplayarena.domain.model.EventStatus — NEW, IN_PROGRESS, COMPLETED, CANCELLED
private fun eventStatusLabel(status: String): String = when (status.uppercase().trim()) {
    "NEW", "UPCOMING", "SCHEDULED" -> "Предстоит"
    "IN_PROGRESS", "ACTIVE", "LIVE", "ONGOING" -> "Идёт"
    "COMPLETED", "FINISHED", "DONE" -> "Завершено"
    "CANCELLED", "CANCELED" -> "Отменено"
    else -> status.lowercase().replaceFirstChar { it.uppercase() }
}

// ── Participants fill-rate indicator colour ───────────────────────────────────

private fun participantColor(participants: Int, max: Int): String {
    val pct = if (max > 0) participants.toDouble() / max else 0.0
    return when {
        pct >= 1.0 -> AppColors.Success.toRgbString()
        pct >= 0.75 -> AppColors.Warning.toRgbString()
        else -> AppColors.Muted.toRgbString()
    }
}

// ─────────────────────────────────────────────────────────────────────────────

@Page("/events")
@Composable
fun EventsPage() {
    val strings = LocalAppStrings.current
    val repo = LocalAppContainer.current.adminRepository ?: return
    val ctx = rememberPageContext()

    val scope = rememberCoroutineScope()
    var period by remember { mutableStateOf(periodFromUrl() ?: Period.MONTH) }
    var page by remember { mutableStateOf(urlParam("page")?.toIntOrNull()?.coerceAtLeast(0) ?: 0) }
    var query by remember { mutableStateOf(urlParam("q") ?: "") }
    var pageSize by remember {
        mutableStateOf(urlParam("size")?.toIntOrNull()?.takeIf { it in PAGE_SIZE_OPTIONS } ?: 25)
    }
    var statusFilter by remember {
        mutableStateOf(urlParam("status") ?: "")
    }
    var exporting by remember { mutableStateOf(false) }
    var showCreate by remember { mutableStateOf(false) }
    var templates by remember { mutableStateOf<List<AdminEventTemplate>>(emptyList()) }
    var sports by remember { mutableStateOf<List<SportOption>>(emptyList()) }

    // ── Remote data ───────────────────────────────────────────────────────────
    var data by remember { mutableStateOf<PageResult<EventRow>?>(null) }
    var popularity by remember { mutableStateOf<List<CategoryShare>?>(null) }
    var sportSizes by remember { mutableStateOf<List<SportEventSize>?>(null) }
    var cities by remember { mutableStateOf<List<CityEventCount>?>(null) }
    var lifecycle by remember { mutableStateOf<EventLifecycleStats?>(null) }
    val loadState = rememberLoadState()

    // ── Best-effort load of sports list ──────────────────────────────────────
    LaunchedEffect(Unit) {
        runCatching { sports = repo.getSports() }
    }

    // ── URL state sync ────────────────────────────────────────────────────────
    LaunchedEffect(period, page, query, pageSize, statusFilter) {
        setUrlParams(
            "period" to period.code.takeIf { period != Period.MONTH },
            "page" to page.takeIf { it > 0 }?.toString(),
            "q" to query.ifBlank { null },
            "size" to pageSize.takeIf { it != 25 }?.toString(),
            "status" to statusFilter.ifBlank { null },
        )
    }

    // ── Load analytics (charts + lifecycle KPIs) ──────────────────────────────
    LaunchedEffect(period, loadState.reloadToken) {
        popularity = null
        lifecycle = null
        loadState.load {
            popularity = repo.getSportPopularity(period)
            lifecycle = repo.getEventLifecycle()
        }
    }

    // ── Load sport-size and city tables (period-independent) ──────────────────
    LaunchedEffect(loadState.reloadToken) {
        sportSizes = null
        cities = null
        loadState.load {
            sportSizes = repo.getSportEventSizes()
            cities = repo.getEventsByCity(15)
        }
    }

    // ── Load events list ──────────────────────────────────────────────────────
    // statusFilter is client-side only — removed from key to avoid spurious refetch
    LaunchedEffect(period, page, query, pageSize, loadState.reloadToken) {
        data = null
        loadState.load {
            data = repo.getEvents(page, pageSize, query)
        }
    }

    AdminLayout(
        title = strings.navEvents,
        subtitle = "Матчи и организованные события платформы",
        actions = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                PeriodSelector(period) { period = it }
                HSpace(8)
                FormButton("Создать ивент", enabled = true, primary = true) {
                    showCreate = true
                }
            }
        }
    ) {
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }

        // ── Lifecycle KPI strip ───────────────────────────────────────────────
        val lc = lifecycle
        if (lc != null) {
            Row(
                Modifier.fillMaxWidth().styleModifier { property("gap", "12px") }
            ) {
                LifecycleKpiTile(
                    label = "Всего событий",
                    value = formatNumber(lc.totalEvents),
                    modifier = Modifier.weight(1f)
                )
                LifecycleKpiTile(
                    label = "Ср. заполняемость",
                    value = "${(lc.avgFillRate * 100).let { kotlin.math.round(it).toInt() }}%",
                    modifier = Modifier.weight(1f)
                )
                LifecycleKpiTile(
                    label = "Ср. время до старта",
                    value = "${kotlin.math.round(lc.avgLeadTimeHours).toInt()} ч",
                    modifier = Modifier.weight(1f)
                )
                LifecycleKpiTile(
                    label = "Завершено",
                    value = "${(lc.completionRate * 100).let { kotlin.math.round(it).toInt() }}%",
                    modifier = Modifier.weight(1f)
                )
                LifecycleKpiTile(
                    label = "Отменено",
                    value = "${(lc.cancellationRate * 100).let { kotlin.math.round(it).toInt() }}%",
                    modifier = Modifier.weight(1f)
                )
            }
            VSpace(16)
        } else {
            // Skeleton KPI strip while loading
            Row(
                Modifier.fillMaxWidth().styleModifier { property("gap", "12px") }
            ) {
                repeat(5) {
                    Box(
                        Modifier.weight(1f).height(70.px).sBorderRadius(12)
                            .styleModifier {
                                property("background", "rgba(255,255,255,.03)")
                                property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                            }
                    )
                }
            }
            VSpace(16)
        }

        // ── Sport popularity chart ────────────────────────────────────────────
        ChartCard(
            title = strings.sectionSportPopularity,
            subtitle = "Доля событий по видам спорта за период"
        ) {
            if (!popularity.isNullOrEmpty()) {
                BarColumns(
                    buckets = popularity!!.map { BarBucket(it.name, it.value) },  // raw counts — BarColumns normalizes internally
                    colorHex = "#0061FF",
                    heightPx = 180,
                    modifier = Modifier.fillMaxWidth(),
                )
            } else {
                Box(
                    Modifier.fillMaxWidth().height(180.px).sBorderRadius(8)
                        .styleModifier {
                            property("background", "rgba(255,255,255,.03)")
                            property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                        }
                )
            }
        }

        VSpace(20)

        // ── Sport event sizes table ───────────────────────────────────────────
        SectionCard(
            title = "Размер событий по виду спорта",
            subtitle = "Реальное число участников — независимо от настроенного максимума",
            modifier = Modifier.fillMaxWidth()
        ) {
            DataTable(
                items = sportSizes.orEmpty(),
                loading = sportSizes == null,
                columns = listOf(
                    TableColumn<SportEventSize>(
                        header = "ВИД СПОРТА",
                        weight = 1.6f,
                        sortKey = { it.sport }
                    ) { row ->
                        Pill(
                            text = row.sport.lowercase().replaceFirstChar { it.uppercase() },
                            tone = sportPillTone(row.sport)
                        )
                    },
                    TableColumn<SportEventSize>(
                        header = "СОБЫТИЙ",
                        weight = 1f,
                        sortKey = { it.eventCount }
                    ) {
                        SpanText(it.eventCount.toString(), Modifier.numSmall().color(AppColors.Muted))
                    },
                    TableColumn<SportEventSize>(
                        header = "МИН",
                        weight = 0.9f,
                        sortKey = { it.minPlayers }
                    ) {
                        SpanText(
                            it.minPlayers.toString(),
                            Modifier.numSmall().color(AppColors.Success)
                        )
                    },
                    TableColumn<SportEventSize>(
                        header = "СРЕДНЕЕ",
                        weight = 0.9f,
                        sortKey = { it.avgPlayers }
                    ) {
                        SpanText(
                            (kotlin.math.round(it.avgPlayers * 10.0) / 10.0).toString(),
                            Modifier.numSmall().color(AppColors.Ink)
                                .styleModifier { property("font-weight", "700") }
                        )
                    },
                    TableColumn<SportEventSize>(
                        header = "МАКС",
                        weight = 0.9f,
                        sortKey = { it.maxPlayers }
                    ) {
                        SpanText(
                            it.maxPlayers.toString(),
                            Modifier.numSmall().color(AppColors.Error)
                        )
                    },
                ),
                emptyText = strings.emptyData,
            )
        }

        VSpace(20)

        // ── Top cities chart ──────────────────────────────────────────────────
        SectionCard(
            title = "Топ городов",
            subtitle = "Города с наибольшим числом созданных событий",
            modifier = Modifier.fillMaxWidth()
        ) {
            CitiesBars(cities)
        }

        VSpace(20)

        // ── Events list ───────────────────────────────────────────────────────
        // Client-side status filter: status filter chips + search work over the
        // loaded page. Full-text search is server-supported (query param passed
        // to repo.getEvents). Status filter is client-side only.

        // Each chip stores a canonical backend value; synonyms guard against non-standard server strings.
        val statusSynonyms = mapOf(
            "NEW"         to setOf("NEW", "UPCOMING", "SCHEDULED"),
            "IN_PROGRESS" to setOf("IN_PROGRESS", "ACTIVE", "LIVE", "ONGOING"),
            "COMPLETED"   to setOf("COMPLETED", "FINISHED", "DONE"),
            "CANCELLED"   to setOf("CANCELLED", "CANCELED"),
        )
        val allItems = data?.items.orEmpty()
        val filteredItems = if (statusFilter.isBlank()) {
            allItems
        } else {
            val matchSet = statusSynonyms[statusFilter.uppercase()] ?: setOf(statusFilter.uppercase())
            allItems.filter { it.status.uppercase() in matchSet }
        }

        SectionCard(
            modifier = Modifier.fillMaxWidth(),
            title = "Список событий",
            subtitle = "Все события платформы с поиском и фильтрацией",
            actions = {
                ExportCsvButton(exporting = exporting) {
                    exporting = true
                    scope.launch {
                        try {
                            val all = fetchAllPages { p, size -> repo.getEvents(p, size, query) }
                            downloadCsv(
                                filename = "events.csv",
                                header = listOf(
                                    "ID", "Название", "Вид спорта", "Город", "Организатор", "PRO",
                                    "Участников", "Макс. участников", "Начало", "Статус",
                                ),
                                rows = all.map {
                                    listOf(
                                        it.id, it.title, it.sport, it.city.orEmpty(),
                                        it.organizer, if (it.isPro) "да" else "нет",
                                        it.participants.toString(), it.maxParticipants.toString(),
                                        it.startAt, it.status,
                                    )
                                },
                            )
                        } finally {
                            exporting = false
                        }
                    }
                }
                HSpace(8)
                SearchInput(
                    value = query,
                    onValueChange = { query = it; page = 0 },
                    placeholderText = "Поиск по названию, спорту, организатору…",
                )
            }
        ) {
            // ── Status filter chips ────────────────────────────────────────────
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                StatusFilterChip("Все", statusFilter.isBlank()) { statusFilter = "" }
                HSpace(6)
                StatusFilterChip("Предстоит", statusFilter.uppercase() == "NEW") {
                    statusFilter = if (statusFilter.uppercase() == "NEW") "" else "NEW"
                    page = 0
                }
                HSpace(6)
                StatusFilterChip("Идёт", statusFilter.uppercase() == "IN_PROGRESS") {
                    statusFilter = if (statusFilter.uppercase() == "IN_PROGRESS") "" else "IN_PROGRESS"
                    page = 0
                }
                HSpace(6)
                StatusFilterChip("Завершено", statusFilter.uppercase() == "COMPLETED") {
                    statusFilter = if (statusFilter.uppercase() == "COMPLETED") "" else "COMPLETED"
                    page = 0
                }
                HSpace(6)
                StatusFilterChip("Отменено", statusFilter.uppercase() == "CANCELLED") {
                    statusFilter = if (statusFilter.uppercase() == "CANCELLED") "" else "CANCELLED"
                    page = 0
                }
            }

            VSpace(12)

            // Result count sub-header
            if (data != null) {
                val total = data?.total ?: 0
                SpanText(
                    if (query.isNotBlank())
                        "Найдено: $total событий (по первым ${pageSize})"
                    else if (statusFilter.isNotBlank())
                        "Отфильтровано: ${filteredItems.size} из ${allItems.size}"
                    else
                        "Всего: $total событий",
                    Modifier.bodySMedium().color(AppColors.Muted)
                )
                VSpace(10)
            }

            DataTable(
                items = filteredItems,
                loading = data == null,
                onRowClick = { row -> ctx.router.navigateTo(Routes.eventDetails(row.id)) },
                columns = listOf(
                    // Column 1: organizer avatar + event title + organizer name
                    TableColumn<EventRow>(
                        header = "СОБЫТИЕ",
                        weight = 2.4f,
                        sortKey = { it.title }
                    ) { row ->
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Box(
                                Modifier.width(34.px).height(34.px).borderRadius(50.percent)
                                    .styleModifier {
                                        property("background", organizerAvatarGradient(row.organizer))
                                        property("flex-shrink", "0")
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    organizerInitials(row.organizer),
                                    Modifier.bodySMedium()
                                        .color(AppColors.Surface)
                                        .styleModifier {
                                            property("font-weight", "700")
                                            property("font-size", "11px")
                                            property("line-height", "1")
                                        }
                                )
                            }
                            HSpace(10)
                            Column {
                                SpanText(row.title, Modifier.bodyMMedium().color(AppColors.Ink))
                                Box(Modifier.height(2.px))
                                SpanText(
                                    "от ${row.organizer}",
                                    Modifier.bodySMedium().color(AppColors.Muted)
                                        .styleModifier { property("font-size", "11px") }
                                )
                            }
                        }
                    },

                    // Column 2: sport type Pill
                    TableColumn<EventRow>(
                        header = "СПОРТ",
                        weight = 1.1f,
                        sortKey = { it.sport }
                    ) { row ->
                        Pill(
                            text = row.sport.lowercase().replaceFirstChar { it.uppercase() },
                            tone = sportPillTone(row.sport)
                        )
                    },

                    // Column 3: city
                    TableColumn<EventRow>(
                        header = "ГОРОД",
                        weight = 1f,
                    ) { row ->
                        SpanText(
                            row.city?.takeIf { it.isNotBlank() } ?: "—",
                            Modifier.bodyMRegular().color(AppColors.Muted)
                        )
                    },

                    // Column 4: participants fill indicator
                    TableColumn<EventRow>(
                        header = "УЧАСТНИКИ",
                        weight = 1f,
                        sortKey = { it.participants }
                    ) { row ->
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            SpanText(
                                "${row.participants}/${row.maxParticipants}",
                                Modifier.bodyMRegular()
                                    .styleModifier {
                                        property("color", participantColor(row.participants, row.maxParticipants))
                                        property("font-weight", "600")
                                        property("font-variant-numeric", "tabular-nums")
                                    }
                            )
                        }
                    },

                    // Column 5: PRO badge
                    TableColumn<EventRow>(
                        header = "PRO",
                        weight = 0.6f,
                    ) { row ->
                        if (row.isPro) Pill("PRO", PillTone.Pro)
                        else SpanText("—", Modifier.bodyMRegular().color(AppColors.Faint))
                    },

                    // Column 6: start time
                    TableColumn<EventRow>(
                        header = "НАЧАЛО",
                        weight = 1.4f,
                        sortKey = { it.startAt }
                    ) { row ->
                        SpanText(
                            formatTimestampShort(row.startAt),
                            Modifier.bodyMRegular().color(AppColors.Muted)
                                .styleModifier { property("font-size", "11px") }
                        )
                    },

                    // Column 7: status Pill
                    TableColumn<EventRow>(
                        header = "СТАТУС",
                        weight = 1f,
                        sortKey = { it.status }
                    ) { row ->
                        Pill(
                            text = eventStatusLabel(row.status),
                            tone = eventStatusPillTone(row.status)
                        )
                    },
                ),
                emptyText = strings.emptyData,
            )
        }

        VSpace(12)

        PaginationBar(
            page = page,
            pageSize = pageSize,
            total = data?.total ?: 0,
            onPrev = { if (page > 0) page-- },
            onNext = {
                val total = data?.total ?: 0
                if ((page + 1) * pageSize < total) page++
            },
            onPageSizeChange = { pageSize = it; page = 0 }
        )

        VSpace(28)
    }

    if (showCreate) {
        CreateEventModal(
            repo = repo,
            sports = sports,
            initialTemplates = templates,
            onTemplatesUpdated = { templates = it },
            onClose = { showCreate = false },
            onCreated = { eventId ->
                showCreate = false
                ctx.router.navigateTo(Routes.eventDetails(eventId))
            },
        )
    }
}

// ── Create-event modal ────────────────────────────────────────────────────────

@Composable
private fun CreateEventModal(
    repo: AdminRepository,
    sports: List<SportOption>,
    initialTemplates: List<AdminEventTemplate>,
    onTemplatesUpdated: (List<AdminEventTemplate>) -> Unit,
    onClose: () -> Unit,
    onCreated: (String) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val toast = LocalToastState.current

    // Form state — reset each time modal opens
    var title by remember { mutableStateOf("") }
    var sportId by remember { mutableStateOf("") }
    var customSportName by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var privacy by remember { mutableStateOf("PUBLIC") }
    var format by remember { mutableStateOf("") }
    var teamsCount by remember { mutableStateOf("") }
    var maxPlayers by remember { mutableStateOf("") }
    var cost by remember { mutableStateOf("0.00") }
    var imageUrl by remember { mutableStateOf("") }
    var dateTimeLocal by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var latitude by remember { mutableStateOf<Double?>(null) }
    var longitude by remember { mutableStateOf<Double?>(null) }
    var city by remember { mutableStateOf<String?>(null) }
    var isPro by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Template management
    var localTemplates by remember { mutableStateOf(initialTemplates) }
    // Selected template — one-shot server-side create: organizer, teams and rosters
    // come from the snapshot, only the date is asked. Null = from-scratch form.
    var selectedTemplate by remember { mutableStateOf<AdminEventTemplate?>(null) }
    var pendingDeleteTemplate by remember { mutableStateOf<AdminEventTemplate?>(null) }
    var deletingTemplate by remember { mutableStateOf(false) }

    // Load templates + sports on open
    LaunchedEffect(Unit) {
        runCatching { localTemplates = repo.getEventTemplates().also { onTemplatesUpdated(it) } }
    }

    val canCreate =
        if (selectedTemplate != null) {
            dateTimeLocal.isNotBlank() && !busy
        } else {
            title.isNotBlank() &&
                sportId.isNotBlank() &&
                dateTimeLocal.isNotBlank() &&
                address.isNotBlank() &&
                latitude != null &&
                longitude != null &&
                !busy
        }

    Modal(
        isOpen = true,
        onClose = { if (!busy) onClose() },
        title = "Создать ивент",
        maxWidth = 640.px,
        closeOnOverlayClick = false,
    ) {
        Column(
            Modifier.fillMaxWidth().styleModifier {
                property("max-height", "70vh")
                property("overflow-y", "auto")
                property("padding-right", "6px")
            }
        ) {
            // ── Template picker ───────────────────────────────────────────────
            if (localTemplates.isNotEmpty()) {
                SpanText(
                    "Создать по шаблону",
                    Modifier.bodyMMedium().color(AppColors.Ink)
                        .styleModifier { property("font-weight", "700") }
                )
                Box(Modifier.height(8.px))
                localTemplates.forEach { template ->
                    Row(
                        Modifier.fillMaxWidth()
                            .padding(topBottom = 7.px, leftRight = 10.px)
                            .sBorderRadius(8)
                            .styleModifier {
                                property("background", AppColors.Background.toRgbString())
                                property("border", "1px solid ${AppColors.Ink.toRgbaString(0.08f)}")
                                property("cursor", "pointer")
                                property("transition", "background 120ms ease")
                            }
                            .onClick {
                                selectedTemplate = if (selectedTemplate?.id == template.id) null else template
                            },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Box(Modifier.weight(1f)) {
                            val totalPlayers = template.payload.teams.sumOf { it.members.size } +
                                template.payload.participants.size
                            val teamsInfo = buildString {
                                if (template.payload.teams.isNotEmpty()) append(" · ${template.payload.teams.size} ком.")
                                if (totalPlayers > 0) append(" · $totalPlayers игр.")
                            }
                            val organizerInfo = template.payload.organizerUsername
                                ?.let { " · орг. @$it" }
                                .orEmpty()
                            SpanText(
                                "${template.name} · ${template.payload.sportName}$organizerInfo$teamsInfo" +
                                    if (selectedTemplate?.id == template.id) " ✓" else "",
                                Modifier.bodyMRegular().color(AppColors.Ink),
                            )
                        }
                        Box(
                            Modifier
                                .padding(leftRight = 6.px, topBottom = 3.px)
                                .sBorderRadius(999)
                                .cursor(Cursor.Pointer)
                                .styleModifier {
                                    property("background", AppColors.Ink.toRgbaString(0.07f))
                                }
                                .onClick {
                                    it.stopPropagation()
                                    pendingDeleteTemplate = template
                                },
                        ) {
                            SpanText("✕", Modifier.bodySMedium().color(AppColors.Muted))
                        }
                    }
                    Box(Modifier.height(6.px))
                }
                Box(Modifier.height(10.px))
            }

            val tpl = selectedTemplate
            if (tpl != null) {
                // ── Template mode: organizer, teams and rosters come from the snapshot ──
                SpanText(
                    "Ивент будет создан от имени " +
                        (tpl.payload.organizerUsername?.let { "@$it" }
                            ?: "админа (в шаблоне нет организатора — пересохрани его с исходного ивента)") +
                        " с командами и подтверждёнными участниками из шаблона.",
                    Modifier.bodySMedium().color(AppColors.Muted),
                )
                Box(Modifier.height(14.px))
                TextInput(
                    value = dateTimeLocal,
                    onValueChange = { dateTimeLocal = it },
                    label = "Дата и время *",
                    type = InputType.DateTimeLocal,
                )
            } else {
            // ── Form fields (from-scratch) ────────────────────────────────────
            TextInput(title, { title = it }, label = "Название *", placeholder = "Название ивента")
            Box(Modifier.height(14.px))

            SportTypeSelect(sports, sportId, label = "Вид спорта *") {
                sportId = it
                if (it != OTHER_SPORT) customSportName = ""
            }
            if (sportId == OTHER_SPORT) {
                Box(Modifier.height(14.px))
                TextInput(
                    customSportName, { customSportName = it },
                    label = "Название спорта *", placeholder = "например, Падел",
                )
            }

            Box(Modifier.height(14.px))
            TextArea(
                description, { description = it },
                label = "Описание", placeholder = "Краткое описание (необязательно)", rows = 3,
            )

            Box(Modifier.height(14.px))
            // Privacy selector using SportTypeSelect-style native Select
            Column(Modifier.fillMaxWidth()) {
                SpanText(
                    "Приватность",
                    Modifier.fontSize(14.px).color(Color("#5D6472")).margin(bottom = 8.px),
                )
                org.jetbrains.compose.web.dom.Select({
                    style {
                        width(100.percent)
                        padding(12.px)
                        property("border-radius", "8px")
                        fontSize(16.px)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        property("font-family", "inherit")
                        property("background-color", "#FFFFFF")
                        property("color", "#0F1222")
                        property("border", "1px solid rgba(15,18,34,0.10)")
                        property("cursor", "pointer")
                    }
                    onChange { privacy = it.value ?: "PUBLIC" }
                }) {
                    org.jetbrains.compose.web.dom.Option("PUBLIC", { if (privacy == "PUBLIC") attr("selected", "") }) {
                        org.jetbrains.compose.web.dom.Text("Публичный")
                    }
                    org.jetbrains.compose.web.dom.Option("PRIVATE", { if (privacy == "PRIVATE") attr("selected", "") }) {
                        org.jetbrains.compose.web.dom.Text("Приватный")
                    }
                }
            }

            Box(Modifier.height(14.px))
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(format, { format = it }, label = "Формат (необязательно)", placeholder = "например, 5")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(teamsCount, { teamsCount = it }, label = "Команд (необязательно)", placeholder = "например, 2")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(maxPlayers, { maxPlayers = it }, label = "Макс. игроков (необязательно)", placeholder = "например, 10")
                }
            }

            Box(Modifier.height(14.px))
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(cost, { cost = it }, label = "Стоимость", placeholder = "0.00")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(imageUrl, { imageUrl = it }, label = "URL изображения (необязательно)", placeholder = "https://…")
                }
            }

            Box(Modifier.height(14.px))
            // Дата и время — используем InputType.DateTimeLocal через TextInput
            TextInput(
                value = dateTimeLocal,
                onValueChange = { dateTimeLocal = it },
                label = "Дата и время *",
                type = InputType.DateTimeLocal,
            )

            Box(Modifier.height(14.px))
            AddressAutocompleteField(
                value = address,
                onValueChange = { address = it; latitude = null; longitude = null; city = null },
                onResolved = { resolved ->
                    address = resolved.address
                    latitude = resolved.latitude
                    longitude = resolved.longitude
                    city = resolved.city
                },
                autocomplete = { q, s -> repo.autocompleteAddress(q, s) },
                resolvePlace = { p, s -> repo.getPlaceAddress(p, s) },
                label = "Адрес *",
                latitude = latitude,
                longitude = longitude,
            )
            CoordinatesHint(latitude, longitude)

            Box(Modifier.height(14.px))
            // PRO toggle (chip)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    Modifier
                        .sBorderRadius(999)
                        .padding(leftRight = 14.px, topBottom = 7.px)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            if (isPro) {
                                property("background", AppColors.Ink.toRgbString())
                            } else {
                                property("background", AppColors.Background.toRgbString())
                                property("border", "1px solid ${AppColors.Ink.toRgbaString(0.10f)}")
                            }
                            property("transition", "all 140ms ease")
                        }
                        .onClick { isPro = !isPro },
                ) {
                    SpanText(
                        "PRO событие",
                        Modifier.bodySMedium()
                            .color(if (isPro) AppColors.Surface else AppColors.Muted)
                            .styleModifier { if (isPro) property("font-weight", "600") },
                    )
                }
            }
            } // end from-scratch form

            if (error != null) {
                Box(Modifier.height(12.px))
                SpanText(error!!, Modifier.bodySMedium().color(AppColors.Error))
            }
        }

        Box(Modifier.height(20.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.weight(1f))
            FormButton("Отмена", enabled = !busy, primary = false) { onClose() }
            Box(Modifier.width(10.px))
            FormButton(
                if (busy) "Создание…" else "Создать",
                enabled = canCreate,
                primary = true,
            ) {
                busy = true
                error = null
                scope.launch {
                    try {
                        val isoDateTime = kotlin.js.Date(dateTimeLocal).toISOString()
                        val tplSelected = selectedTemplate
                        if (tplSelected != null) {
                            val r = repo.createEventFromTemplate(tplSelected.id, isoDateTime)
                            toast.success(
                                "Ивент создан" +
                                    (if (r.teamsCreated > 0) ": команд ${r.teamsCreated}, игроков ${r.playersAdded}" else "") +
                                    if (r.playersSkipped > 0) " (пропущено ${r.playersSkipped})" else "",
                            )
                            if (!r.organizerApplied) {
                                toast.error("В шаблоне нет организатора — создатель ивента: админ. Пересохрани шаблон с исходного ивента.")
                            }
                            onCreated(r.eventId)
                        } else {
                            val lat = latitude
                            val lon = longitude
                            if (lat == null || lon == null) {
                                busy = false
                                return@launch
                            }
                            val created = repo.createEvent(
                                CreateEventPayload(
                                    title = title.trim(),
                                    sportId = sportId,
                                    customSportName = customSportName.trim().ifBlank { null },
                                    description = description.trim().ifBlank { null },
                                    privacy = privacy,
                                    format = format.trim().toIntOrNull(),
                                    teamsCount = teamsCount.trim().toIntOrNull(),
                                    maxPlayers = maxPlayers.trim().toIntOrNull(),
                                    cost = cost.trim().ifBlank { "0.00" },
                                    imageUrl = imageUrl.trim().ifBlank { null },
                                    dateTime = isoDateTime,
                                    latitude = lat.toString(),
                                    longitude = lon.toString(),
                                    address = address.trim(),
                                    city = city,
                                    isPro = isPro,
                                )
                            )
                            toast.success("Ивент создан")
                            onCreated(created.id)
                        }
                    } catch (e: Throwable) {
                        error = e.message ?: "Не удалось создать ивент"
                        busy = false
                    }
                }
            }
        }
    }

    // Delete-template confirmation overlay
    ConfirmActionOverlay(
        isOpen = pendingDeleteTemplate != null,
        onClose = { if (!deletingTemplate) pendingDeleteTemplate = null },
        title = "Удалить шаблон?",
        description = "Шаблон «${pendingDeleteTemplate?.name.orEmpty()}» будет удалён без возможности восстановления.",
        confirmLabel = if (deletingTemplate) "Удаление…" else "Удалить",
        confirmDanger = true,
        confirmEnabled = !deletingTemplate,
        onConfirm = {
            val tpl = pendingDeleteTemplate ?: return@ConfirmActionOverlay
            deletingTemplate = true
            scope.launch {
                try {
                    repo.deleteEventTemplate(tpl.id)
                    val updated = localTemplates.filter { it.id != tpl.id }
                    localTemplates = updated
                    onTemplatesUpdated(updated)
                    toast.success("Шаблон удалён")
                } catch (e: Throwable) {
                    toast.error(e.message ?: "Не удалось удалить шаблон")
                } finally {
                    deletingTemplate = false
                    pendingDeleteTemplate = null
                }
            }
        },
    )
}

// ── Lifecycle KPI tile ────────────────────────────────────────────────────────

@Composable
private fun LifecycleKpiTile(
    label: String,
    value: String,
    modifier: Modifier = Modifier
) {
    Box(
        modifier
            .sBorderRadius(12)
            .styleModifier {
                property("background", AppColors.Surface.toRgbString())
                property("border", "1px solid ${AppColors.Border.toRgbString()}")
                property("box-shadow", "0 1px 2px rgba(0,0,0,.18)")
            }
            .padding(leftRight = 18.px, topBottom = 14.px)
    ) {
        Column {
            SpanText(label, Modifier.bodySMedium().color(AppColors.Faint)
                .styleModifier { property("font-size", "12px"); property("font-weight", "600") })
            Box(Modifier.height(6.px))
            SpanText(value, Modifier.numSmall().color(AppColors.Ink)
                .styleModifier { property("font-size", "20px"); property("font-weight", "700") })
        }
    }
}

// ── Status filter chip ────────────────────────────────────────────────────────

@Composable
private fun StatusFilterChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .padding(leftRight = 12.px, topBottom = 5.px)
            .sBorderRadius(999)
            .styleModifier {
                if (active) {
                    property("background", "rgba(0,97,255,.28)")
                    property("box-shadow", "0 1px 2px rgba(0,0,0,.28)")
                } else {
                    property("background", "rgba(255,255,255,.05)")
                }
                property("cursor", "pointer")
                property("transition", "all 160ms ease")
            }
            .onClick { onClick() }
    ) {
        SpanText(
            label,
            Modifier.bodySMedium()
                .color(if (active) AppColors.PrimaryLight else AppColors.Muted)
                .styleModifier { if (active) property("font-weight", "600") }
        )
    }
}

// ── Top cities bar chart ──────────────────────────────────────────────────────

@Composable
private fun CitiesBars(cities: List<CityEventCount>?) {
    if (cities == null) {
        Box(
            Modifier.fillMaxWidth().height(160.px)
                .sBorderRadius(8)
                .styleModifier {
                    property("background", "rgba(255,255,255,.03)")
                    property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                }
        )
        return
    }
    if (cities.isEmpty()) {
        Box(
            Modifier.fillMaxWidth().padding(topBottom = 36.px),
            contentAlignment = Alignment.Center
        ) {
            SpanText("Нет данных", Modifier.bodyMRegular().color(AppColors.Muted))
        }
        return
    }
    val max = cities.maxOf { it.eventCount }.coerceAtLeast(1L)
    Column(Modifier.fillMaxWidth()) {
        cities.forEachIndexed { idx, c ->
            val widthPct = (c.eventCount.toDouble() / max * 100.0).coerceIn(2.0, 100.0)
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.width(150.px)) {
                    SpanText(c.city, Modifier.bodyMMedium().color(AppColors.Ink))
                }
                Box(
                    Modifier
                        .weight(1f)
                        .height(22.px)
                        .styleModifier {
                            property("background", "rgba(255,255,255,.06)")
                            property("border-radius", "6px")
                            property("overflow", "hidden")
                            property("position", "relative")
                        },
                ) {
                    Box(
                        Modifier
                            .height(22.px)
                            .styleModifier {
                                property("width", "${widthPct}%")
                                property("background", AppColors.Primary.toRgbString())
                                property("border-radius", "6px")
                                property("transition", "width 240ms ease")
                            },
                    )
                }
                HSpace(12)
                Box(Modifier.width(60.px)) {
                    SpanText(
                        c.eventCount.toString(),
                        Modifier.bodySMedium().color(AppColors.Ink)
                            .styleModifier { property("font-weight", "600") }
                    )
                }
            }
            if (idx != cities.lastIndex) VSpace(8)
        }
    }
}
